import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Link, useLocation } from 'react-router-dom'
import { GSIcon } from 'src/components/shared/GSIcon'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { useKillswitchStates } from 'src/utils/hooks/paymentsOps/useKillswitchStates'

/**
 * Shown above every Payments Ops page while a killswitch is GLOBALLY active (spec §4.4).
 * Partner-only rows do not raise it — the Killswitch page lists those.
 */
const KillswitchBannerComponent = () => {
  const { pathname } = useLocation()
  const { payments, webhooks } = useKillswitchStates()

  const message = useMemo(() => {
    const parts: string[] = []

    if (payments.isGloballyActive) {
      parts.push('Payment processing is stopped platform-wide')
    }

    if (webhooks.isGloballyActive) {
      parts.push('US Bank webhooks are disabled platform-wide')
    }

    return parts.join(' · ')
  }, [payments.isGloballyActive, webhooks.isGloballyActive])

  if (message === '') {
    return null
  }

  const isOnKillswitchPage = pathname === ADMIN_ROUTES.PAYMENTS_OPS_KILLSWITCH_URL

  return (
    <div
      role="alert"
      data-testid="payments-ops-killswitch-banner"
      css={tw`flex shrink-0 flex-row items-center gap-3 border-0 border-b border-solid border-critical-100 bg-critical-50 px-6 py-3`}
    >
      <GSIcon iconName="gs_triangle_exclamation" size={20} css={tw`text-critical-500`} />
      <Typography variant="body2" css={tw`flex-1 text-critical-500`}>
        {message}.
      </Typography>
      {isOnKillswitchPage ? null : (
        <Link
          to={ADMIN_ROUTES.PAYMENTS_OPS_KILLSWITCH_URL}
          css={tw`text-sm font-semibold text-critical-500`}
        >
          Go to Killswitch
        </Link>
      )}
    </div>
  )
}

export const KillswitchBanner = memo(KillswitchBannerComponent)
